<template>
    <div id="wrapper">
        <div class="fullscreen background"></div>
        <header>
            <div class="section header-section">
                <h2> Match Settings </h2>
            </div>
        </header>
        <main>
            <section class="section">
                <!-- Team Settings -->
                <h3> Choose Your Team </h3>
                <strong> Select the team to play: </strong>
                <label class="block">
                    <input type="radio" value="red" v-model="selectedTeam">
                    <b> Red Team </b> (First to start the game)
                </label>
                <label class="block">
                    <input type="radio" value="blue" v-model="selectedTeam">
                    <b> Blue Team </b> (Second to start the game)
                </label>
            </section>
            <section class="section">
                <!-- Game Settings -->
                <h3> Configure Your Game </h3>
                <div class="inner-section">
                    <strong> Select a map: </strong>
                    <label class="block">
                        <input type="radio" value="default" v-model="selectedMap">
                        Default Map
                    </label>
                    <strong> Select a game mode: </strong>
                    <label v-for="(mode, index) in gameModes" :key="index" class="block">
                         <input type="radio" :value="mode.id" v-model="selectedGameMode" class="margin">
                         <b> {{ mode.name }} - </b> {{ mode.description }}
                    </label>
                </div>
            </section>
            <section class="section">
                <!-- Unit Loadout Settings -->
                <h3> Confirm Unit Loadout </h3>
                <strong> Confirm Unit Loadout: </strong>
                <label class="block">
                    <input type="checkbox" v-model="useCustomLoadout">
                    <b> Use custom unit loadout </b>
                </label>
                <div class="flexbox" v-if="useCustomLoadout">
                    <button @click="setLoadout('min')" class="menu-btn"> Minimum Loadout </button>
                    <button @click="setLoadout('default')" class="menu-btn"> Default Loadout </button>
                    <button @click="setLoadout('max')" class="menu-btn"> Maximum Loadout </button>
                </div>
                <label v-for="(unit, index) in customLoadout" :key="index" class="block unit">
                    <input type="number" v-model="unit.count" :disabled="!useCustomLoadout" @input="onUnitCountChange(index)">
                    x <b> {{ unit.unit.name }} </b> - {{ unit.unit.type }}
                </label>
            </section>
            <section class="section">
                <!-- Special Moves Section -->
                <h3> Set Special Moves </h3>
                <strong> Enable or disable special moves: </strong>
                <label v-for="(specialMove, index) in specialMoves" :key="index" class="block">
                     <input type="checkbox" v-model="specialMove.activated" class="margin">
                     <b> {{ specialMove.name }} - </b> {{ specialMove.description }}
                </label>
            </section>
            <section class="section">
                <!-- Advanced Settings -->
                <h3> Advanced Settings </h3>
                <div class="inner-section">
                    <strong> Configure prep stage: </strong>
                    <label class="block">
                        <input type="checkbox" v-model="limitPrepStage">
                        <b> Limit prep stage duration </b> (players can still request time extension)
                    </label>
                    <label class="block">
                        <input type="number" v-model="prepStageTimeLimit" :disabled="!limitPrepStage">
                        Prep stage duration (in minutes)
                    </label>
                </div>
                <div>
                    <strong> Set game rules: </strong>
                    <label class="block">
                        <input type="checkbox" v-model="allowNestedVehicles">
                        Allow nested vehicles
                    </label>
                </div>
            </section>
            <p class="warning" v-if="warning.show"> {{ warning.message }} </p>
            <div class="d-grid gap-2 bottom">
                <button id="submit-btn" class="btn" type="button" @click="validate()"> Host Game </button>
            </div>
        </main>
    </div>
</template>

<script>
const config = require('../config');
import { starterUnits } from '../scripts/GameVariables.js';

const gameModes =  [
    {
        name: 'Classic',
        id: 'classic',
        description: "Locate the opponent's flag, capture it and bring it to your island to win the game.",
    },
    {
        name: 'Last Man Standing',
        id: 'lastManStanding',
        description: "Destroy all opponent's non-static units and be the last man standing.",
    },
    {
        name: 'Surprise Attack',
        id: 'surpriseAttack',
        description: "Classic mode, except prep stage is skipped and unit positions are randomized.",
    },
];

const specialMoves =  [
    {
        name: 'Remote Control',
        id: 'remoteControl',
        description: 'Find a random bomb the enemy controls and explode it remotely.',
        activated: false
    },
    {
        name: 'True Identity',
        id: 'trueIdentity',
        description: 'Reveal a unit you are in a position to attack.',
        activated: false
    },
    {
        name: 'Instant Switch',
        id: 'instantSwitch',
        description: 'Switch positions of two allie, non-static troops.',
        activated: false
    },
];

export default {
    inject: ['socket', 'setAuthToken', 'initSocket'],
    methods: {
        onUnitCountChange(unitIndex){
            this.hideWarning();
            const unit = this.customLoadout[unitIndex];
                if(unit.count !== ''){
                    if(unit.count > unit.max){
                    unit.count = unit.max;
                }
                else if(unit.count < unit.min){
                    unit.count = unit.min;
                }
            }
        },
        setLoadout(loadoutName){
            if(!['min', 'max', 'default'].includes(loadoutName)){
                return;
            }
            for(let entry of this.customLoadout){
                entry.count = entry[loadoutName];
            }
        },
        showWarning(message){
            this.warning.message = message;
            this.warning.show = true;
        },
        hideWarning(){
            this.warning.show = false;
        },
        validate(){
            // Checking if custom unit loadout is valid
            const loadout = [];
            const specialMoves = {};
            if(this.useCustomLoadout){
                for(let entry of this.customLoadout){
                    loadout.push({id: entry.unit.id, count: entry.count});
                    if(entry.count === ''){
                        this.showWarning('Please confirm your custom loadout!');
                        return;
                    }
                }
            }
            // Checking if time limit for prep stage is valid
            if(this.limitPrepStage){
                if(this.prepStageTimeLimit === ''){
                    this.showWarning('Please enter a valid time limit!');
                    return;
                }
            }
            for(let move of this.specialMoves){
                specialMoves[move.id] = move.activated;
            }
            // Creating match settings
            const matchSettings = {
                hostTeam: this.selectedTeam,
                map: this.selectedMap,
                gameMode: this.selectedGameMode,
                specialMoves: specialMoves,
                prepStageTimeLimit: this.limitPrepStage ? this.prepStageTimeLimit : -1,
                customLoadout: loadout,
            }
            // Sending request to server
            this.socket = this.initSocket({
                actionType: 'game.host',
                matchSettings
            });
            this.socket.connect();
            this.socket.once('match.initialData', (response) => {
                if(response.authToken){
                    this.setAuthToken(response.authToken);
                }
                if(response.gameId){
                    let uri = `/game/${response.gameId}`;
                    if(config.production) {
                        uri = config.productionRoot + uri;
                    }
                    this.$router.push(uri);
                }
            });
        },
    },
    watch: {
        prepStageTimeLimit(value){
            if(value !== ''){
                if(value < this.prepStageTimeLimits.min){
                this.prepStageTimeLimit = this.prepStageTimeLimits.min;
                }
                else if(value > this.prepStageTimeLimits.max){
                    this.prepStageTimeLimit = this.prepStageTimeLimits.max;
                }
            }
        },
    },
    data(){
        const customLoadout = [];
        for(let unit of starterUnits){
            customLoadout.push({unit: unit.unit(), count: unit.default, max: unit.max, min: unit.min, default: unit.default});
        }
        return {
            prepStageTimeLimits: {
                min: 2,
                max: 15
            },
            selectedTeam: 'red',
            prepStageTimeLimit: 5,
            limitPrepStage: false,
            allowNestedVehicles: false,
            useCustomLoadout: false,
            warning: { message: '', show: false },
            specialMoves: specialMoves,
            gameModes: gameModes,
            selectedGameMode: 'classic',
            customLoadout: customLoadout,
            selectedMap: 'default',
        };
    },
}
</script>

<style scoped>
#wrapper{
    margin-left: 12px;
    margin-right: 12px;
    font-family: 'Jost', sans-serif;
    background: rgba(209, 26, 26, 0);
    color: #eeeeeeea;
}
#submit-btn{
    color: #222831;
    background: #00ADB5;
    opacity: 0.65;
    transition: opacity 0.15s;
}
#submit-btn:hover{
    opacity: 0.7;
}
.menu-btn{
    /* min-width: fit-content; */
    max-width: 170px;
    width: 30%;
    background-color: #00ADB5;
    opacity: 0.8;
    margin-bottom: 10px;
    padding: 1px 10px 1px 10px;
    border-radius: 8px;
    font-size: 1.15em;
    cursor: pointer;
    user-select: none;
    text-align: center;
    margin-right: 10px;
    text-decoration: none;
    display: block;
    color: #222831;
    transition: opacity 0.15s;
    border: none;
}
.menu-btn:hover{
    opacity: 0.9;
}
.background{
    background-color: #222831;
}
h1, h2, h3{
    user-select: none;
}
h3{
    /* color: rgb(17, 99, 175); */
    color: #00ADB5;
    opacity: 0.8;
    margin-bottom: 3px;
}
h2{
    color: #00ADB5;
    opacity: 0.9;
    text-align: center;
}
strong{
    font-size: 1.02em;
}
header{
    position: sticky;
    top: 0px;
    /* background-color: #eeeeee !important; */
    background-color: #222831 !important;
    padding-top: 8px;
    padding-bottom: 8px;
    z-index: 10;
}
header:hover{
    background-color: #eeeeee;
}
.header-section{
    margin-bottom: 0 !important;
}
.section {
    margin-bottom: 8px;
    padding: 5px;
    padding-left: 8px;
    padding-right: 8px;
    border-radius: 5px;
    transition: all 0.2s;
    /* background: rgba(104, 104, 104, 0.25); */
    background-color: #393e46c4;
}
.section:hover, header:hover{
    /* background: rgba(104, 104, 104, 0.4); */
    background-color: #393e46e3;
}
input[type=checkbox]{
    cursor: pointer;
}
input[type=radio]{
    cursor: pointer;
}
input[type='number']{
    width: 40px;
}
p{
    margin-bottom: 3px;
}
.unit{
    margin-bottom: 3px;
}
.inner-section{
    margin-bottom: 3px;
}
label>input{
    margin-right: 0px;
}
.margin{
    margin-right: 5px;
}
.warning{
    color: var(--team-red);
}
</style>